import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { TableTitlesContext } from './table-titles-context'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { getTables } from '../fetch/get-tables'
import type { MatterTable } from '../schemas'

/** Share metadata only for this workspace's lifetime, loading matters when a card needs them. */
export function TableTitlesProvider({ children, refreshKey = '' }: { children: ReactNode; refreshKey?: string }) {
  const { enabled, loading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.TABLES)
  const tablesEnabled = enabled && !loading
  const [tablesByMatter, setTablesByMatter] = useState<Record<string, MatterTable[] | null>>({})
  const requests = useRef(new Map<string, AbortController>())
  const load = useCallback(
    (matterId: string, force = false) => {
      if (!tablesEnabled) return
      const previous = requests.current.get(matterId)
      if (previous && !force) return
      previous?.abort()
      const controller = new AbortController()
      requests.current.set(matterId, controller)
      getTables(matterId, controller.signal)
        .then((tables) => {
          if (!controller.signal.aborted) setTablesByMatter((current) => ({ ...current, [matterId]: tables }))
        })
        .catch(() => {
          if (!controller.signal.aborted) setTablesByMatter((current) => ({ ...current, [matterId]: null }))
        })
    },
    [tablesEnabled]
  )
  const refresh = useCallback(() => {
    for (const matterId of requests.current.keys()) load(matterId, true)
  }, [load])
  const previousRefreshKey = useRef(refreshKey)
  useEffect(() => {
    if (previousRefreshKey.current !== refreshKey) {
      previousRefreshKey.current = refreshKey
      refresh()
    }
  }, [refreshKey, refresh])
  useEffect(() => {
    const current = requests.current
    if (!tablesEnabled) {
      for (const controller of current.values()) controller.abort()
      current.clear()
      setTablesByMatter({})
    }
    return () => {
      for (const controller of current.values()) controller.abort()
      current.clear()
    }
  }, [tablesEnabled])
  return <TableTitlesContext.Provider value={{ tablesByMatter, load, refresh }}>{children}</TableTitlesContext.Provider>
}
